import { useCallback, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

/**
 * How far below the screen the load-more box starts the next load.
 *
 * It used to fire only when the box itself came on screen, and only on that
 * edge. The box sits right above a ~1000px footer, so a fling carried the
 * reader past it into the footer, and when a load added a single row the box
 * barely moved. Either way it never came on screen again and the feed sat
 * still until the reader scrolled back up.
 *
 * The band reaches ABOVE the screen too: a reader who has flung into the
 * footer has the box above them, and they are exactly the one who wants the
 * next page.
 */
export const LOAD_AHEAD_PX = 1500;

export interface LoadAheadOptions {
  /** Fetch and append the next page. Called at most once per band entry or finished load. */
  onLoadMore: () => Promise<unknown> | void;
  loading: boolean;
  done: boolean;
  /** Tiles and rows on screen: what a load appends to. */
  shownCount: number;
}

/**
 * Every paged grid's next-page trigger, so no page loads differently: the
 * box under the grid (`ref`) takes the next page while it is within
 * LOAD_AHEAD_PX of the screen, and `loadMore` is the same call for the
 * button under it. Every page loads ahead (owner, 2026-10-20); For You and
 * the seller page used to wait for a tap.
 */
export function useLoadAhead({ onLoadMore, loading, done, shownCount }: LoadAheadOptions) {
  const shownCountRef = useRef(shownCount);
  const shownAtLoadStartRef = useRef(shownCount);
  const onLoadMoreRef = useRef(onLoadMore);
  const blockedRef = useRef(loading || done);
  useEffect(() => {
    shownCountRef.current = shownCount;
    onLoadMoreRef.current = onLoadMore;
    blockedRef.current = loading || done;
  });

  // Stable across renders: it reads the latest props through the refs.
  const loadMore = useCallback(async () => {
    shownAtLoadStartRef.current = shownCountRef.current;
    await onLoadMoreRef.current();
  }, []);

  const { ref, inView } = useInView({
    threshold: 0,
    delay: 0,
    rootMargin: `${LOAD_AHEAD_PX}px 0px ${LOAD_AHEAD_PX}px 0px`,
    onChange: (iv) => {
      if (iv && !blockedRef.current) void loadMore();
    },
  });

  // `onChange` only fires when the box ENTERS the band. A load that leaves it
  // inside the band (one short row, or a fast reader already near the bottom)
  // would otherwise be the last one. So when a load finishes and the box is
  // still in range, take the next page. Only if that load actually added
  // something: one that added nothing waits for the next scroll, rather than
  // asking the server the same question in a loop.
  useEffect(() => {
    if (!inView || loading || done) return;
    if (shownCount <= shownAtLoadStartRef.current) return;
    void loadMore();
  }, [inView, loading, done, shownCount, loadMore]);

  return { ref, inView, loadMore };
}
